import { useCallback, useEffect, useState } from 'react';
import type { GrowthPayload } from '../../data/growthTypes';
import { growthNavSections, type GrowthNavSection } from '../../data/growthViewModel';

/**
 * What the sidebar lists under Growth: the sections (Website and Local and
 * search only while a Google product feeding them is connected for the
 * venues in view, or in the demo group) and whether Data health needs
 * action. It comes from the connection status a loaded Growth payload
 * carries, kept per venues in view, so switching sections never changes
 * which items show: while a section loads, the last known state stays.
 */
export type GrowthNavState = { sections: GrowthNavSection[]; attention: boolean };

/** A loaded payload's connection status, reported for the venues it was read for. */
export type GrowthNavReport = {
  key: string;
  availability: GrowthPayload['sectionAvailability'] | null;
  attention: boolean;
};

const STORE = 'aleno_growth_nav_v1';
const MAX_KEYS = 30;

/** The venues in view as the key their status is kept under. */
export function growthNavKey(restaurantId: string, regionFilter: string | null | undefined, demo: boolean): string {
  return demo ? 'demo' : `${restaurantId}|${regionFilter || 'all'}`;
}

function readStore(): Record<string, GrowthNavState> {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(STORE);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, GrowthNavState>) : {};
  } catch {
    return {};
  }
}

export function loadGrowthNav(key: string): GrowthNavState | null {
  const found = readStore()[key];
  return found && Array.isArray(found.sections) ? found : null;
}

export function saveGrowthNav(key: string, state: GrowthNavState): void {
  try {
    if (typeof window === 'undefined') return;
    const store = readStore();
    delete store[key];
    store[key] = state;
    const keys = Object.keys(store);
    for (const old of keys.slice(0, Math.max(0, keys.length - MAX_KEYS))) delete store[old];
    window.localStorage.setItem(STORE, JSON.stringify(store));
  } catch {
    // Storage full or blocked: the state still holds for this page.
  }
}

/** Before any status is known: the sections that stand on aleno data alone. */
export const UNKNOWN_GROWTH_NAV: GrowthNavState = { sections: growthNavSections(null), attention: false };

const DEMO_GROWTH_NAV: GrowthNavState = { sections: growthNavSections(null, true), attention: false };

function sameState(a: GrowthNavState, b: GrowthNavState): boolean {
  return a.attention === b.attention && a.sections.join() === b.sections.join();
}

/**
 * The sidebar's Growth state for the venues in view. A new key starts from
 * what was last stored for it, else keeps the previous venues' state until
 * a payload for the new venues reports; a report for another key is ignored.
 */
export function useGrowthNav(key: string, demo: boolean) {
  const [state, setState] = useState<GrowthNavState>(() =>
    demo ? DEMO_GROWTH_NAV : loadGrowthNav(key) ?? UNKNOWN_GROWTH_NAV,
  );
  useEffect(() => {
    const next = demo ? DEMO_GROWTH_NAV : loadGrowthNav(key);
    if (next) setState(prev => (sameState(prev, next) ? prev : next));
  }, [key, demo]);
  const report = useCallback(
    (next: GrowthNavReport) => {
      if (demo || next.key !== key) return;
      const value: GrowthNavState = {
        sections: growthNavSections(next.availability),
        attention: next.attention,
      };
      setState(prev => (sameState(prev, value) ? prev : value));
      saveGrowthNav(key, value);
    },
    [key, demo],
  );
  return { ...state, report };
}
